import type { ReactNode } from 'react';

export interface BadgeStyle {
  color: string;
  bg: string;
  border?: string;
  icon?: ReactNode;
}

export type BadgeStyleMap = Record<string, BadgeStyle>;

const DEFAULT_STYLE: BadgeStyle = { color: '#6b7280', bg: '#f3f4f6' };
export const BADGE_VARIANT_STYLE = {
  info: { color: '#003DE6', bg: '#F7FAFF' },
  entangled: { color: '#4A4742', bg: '#E9FBFF' },
  purpose: { color: '#1F1F1C', bg: '#DFE7FF' },
  consentTag: { color: '#1F1F1C', bg: '#E9FBFF' },
  neutral: { color: '#374151', bg: '#f3f4f6' },
} as const satisfies BadgeStyleMap;

export type BadgeVariant = keyof typeof BADGE_VARIANT_STYLE;

export interface BadgeProps {
  value: ReactNode;
  variant?: BadgeVariant;
  styleMap?: BadgeStyleMap;
  size?: 'sm' | 'md';
  icon?: ReactNode;
  dot?: boolean;
  shape?: 'pill' | 'rounded';
  className?: string;
}

export const Badge = ({
  value,
  variant,
  styleMap = {},
  size = 'md',
  icon,
  dot = false,
  shape = 'pill',
  className = '',
}: BadgeProps) => {
  const style: BadgeStyle =
    (variant && BADGE_VARIANT_STYLE[variant]) ||
    (typeof value === 'string' && styleMap[value]) ||
    DEFAULT_STYLE;
  const sizeClass = size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-0.5 text-[12px]';
  const iconClass = size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5';
  const shapeClass = shape === 'rounded' ? 'rounded-md' : 'rounded-full';
  const resolvedIcon = icon ?? style.icon;
  return (
    <span
      className={`inline-flex items-center justify-center gap-1 font-medium whitespace-nowrap ${shapeClass} ${sizeClass} ${className}`}
      style={{
        color: style.color,
        background: style.bg,
        border: style.border ? `1px solid ${style.border}` : undefined,
      }}
    >
      {dot && (
        <span className="shrink-0 w-1.5 h-1.5 rounded-full" style={{ background: style.color }} />
      )}
      {resolvedIcon && <span className={`shrink-0 ${iconClass}`}>{resolvedIcon}</span>}
      {value ?? '—'}
    </span>
  );
};
